import type { FlowNode, FlowFilterCondition } from '@reflex/flows';
import { FlowFilterOpSchema, PRESENCE_FILTER_OPS } from '@reflex/flows';
import { findCatalogEntry } from '../../../shared/event-catalog';
import { jsonSchemaToFormFields } from '../../../shared/json-schema-validate';
import { FILTER_OP_LABELS } from '../../../shared/describe-filter';

export const FILTER_OPS = FlowFilterOpSchema.options.map((value) => ({
  value,
  label:
    value === 'in' || value === 'intersects'
      ? `${FILTER_OP_LABELS[value]} (comma list)`
      : FILTER_OP_LABELS[value],
}));

export function conditionValueToText(value: FlowFilterCondition['value']): string {
  if (value === undefined) return '';
  return Array.isArray(value) ? value.join(', ') : String(value);
}

export function textToConditionValue(
  op: FlowFilterCondition['op'],
  text: string,
): FlowFilterCondition['value'] {
  if (PRESENCE_FILTER_OPS.has(op) || text === '') return undefined;
  if (op === 'in' || op === 'intersects') {
    return text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return text;
}

/** Coerce an existing value into a shape that fits the newly-selected op. */
export function convertValueForOp(
  op: FlowFilterCondition['op'],
  value: FlowFilterCondition['value'],
): FlowFilterCondition['value'] {
  if (PRESENCE_FILTER_OPS.has(op)) return undefined;
  if (op === 'in' || op === 'intersects') {
    if (Array.isArray(value)) return value;
    if (value === undefined || value === '') return undefined;
    return [String(value)];
  }
  if (Array.isArray(value)) return value.length > 0 ? value[0] : undefined;
  return value;
}

export function fieldTitlesFromSchema(
  payloadSchema: Record<string, unknown> | null | undefined,
): Record<string, string> {
  if (!payloadSchema) return {};
  const fields = jsonSchemaToFormFields(payloadSchema);
  const titles: Record<string, string> = {};
  for (const f of fields) {
    if (f.title) titles[f.name] = f.title;
  }
  return titles;
}

/**
 * Resolve the payload schema a standalone filter node's `trigger.*` paths
 * should type against.
 */
export function triggerPayloadSchema(
  nodes: FlowNode[],
  catalog: Parameters<typeof findCatalogEntry>[0],
): Record<string, unknown> | null {
  const schemas = nodes
    .filter((n): n is FlowNode & { type: 'trigger' } => n.type === 'trigger')
    .map((n) =>
      n.config.kind === 'event' ? findCatalogEntry(catalog, n.config)?.payloadJsonSchema : null,
    )
    .filter((s): s is Record<string, unknown> => Boolean(s));

  if (schemas.length === 0) return null;
  if (schemas.length === 1) return schemas[0];

  const properties: Record<string, unknown> = {};
  for (const schema of schemas) {
    if (schema.properties && typeof schema.properties === 'object') {
      Object.assign(properties, schema.properties as Record<string, unknown>);
    }
  }
  return { type: 'object', properties };
}
